import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Avatar } from "@/components/Avatar/Avatar";
import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { CompanyLogoAvatar } from "@/components/CompanyLogoAvatar/CompanyLogoAvatar";
import { getPrimaryExperience } from "@/components/GlobalSearchModal/utils";
import {
	CompanyEnrichPreviewExperienceCompany,
	CompanyEnrichPreviewPerson,
} from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

import { parseLinkedinProfileUrl } from "../../utils/linkedinProfileUrl";
import { TrackAddPersonCombobox } from "./TrackAddPersonCombobox";

/**
 * The one experience a row is shown under: the matched, else the current one — and only ever one
 * that resolves to a company. Both the Company and the Position cell read it, so the title on
 * screen always belongs to the employer next to it.
 *
 * It deliberately stops there. Falling through to any current-or-first experience printed the title
 * of a company-less entry (military service, a freelance stint) beside an empty Company cell — a
 * row reading "Reservist" under a search for founders and CTOs.
 */
const displayExperience = (person: CompanyEnrichPreviewPerson) => getPrimaryExperience(person);

/**
 * The job title of that experience. The top-level `position` is the LinkedIn
 * headline ("Helping B2B teams scale | ex-Salesforce"), not a title, so it only
 * stands in when the experience carries none — the same precedence the search
 * result cards use (PREC-3669).
 */
const jobTitleOf = (person: CompanyEnrichPreviewPerson): string | null =>
	displayExperience(person)?.position ?? person.position ?? null;

/** The headline, offered on hover when it says something the title does not. */
const headlineOf = (person: CompanyEnrichPreviewPerson): string | null =>
	person.position && person.position !== jobTitleOf(person) ? person.position : null;

const nameParts = (name: string): { firstName: string; lastName: string } => {
	const parts = name.trim().split(" ");

	return { firstName: parts[0] ?? "", lastName: parts[parts.length - 1] ?? "" };
};

const headerClass = "px-3 py-2 text-left text-[11px] font-semibold uppercase text-text-meta";
const cellClass = "px-3 py-2.5 text-ssm";

/**
 * The row-number cell that becomes a checkbox on hover or once the row is
 * selected (mockup `.tk-cbcell`) — the number keeps the table readable while
 * still making every row obviously selectable.
 */
const RowNumberCell: FunctionComponent<{
	index: number;
	isSelected: boolean;
	onToggle: () => void;
	label: string;
}> = ({ index, isSelected, onToggle, label }) => (
	<td className="w-11 px-3">
		<button
			aria-checked={isSelected}
			aria-label={label}
			className="flex w-full cursor-pointer items-center justify-center"
			role="checkbox"
			type="button"
			onClick={(event) => {
				// The whole row toggles too, so letting the click bubble would toggle
				// twice and land back on the original state — the checkbox would look
				// dead while the rest of the row worked (PREC-3719).
				event.stopPropagation();
				onToggle();
			}}
		>
			<span
				className={classNames(
					"text-xs tabular-nums text-text-meta",
					isSelected ? "hidden" : "group-hover:hidden"
				)}
			>
				{index + 1}
			</span>
			<span
				className={classNames(
					"h-4.5 w-4.5 items-center justify-center rounded border text-[10px] font-bold",
					isSelected
						? "flex border-brand-light bg-blue-action text-white"
						: "hidden border-gray-300 text-transparent group-hover:flex"
				)}
			>
				✓
			</span>
		</button>
	</td>
);

/** Company logo + name, with the domain as a link out to the site. */
const CompanyCell: FunctionComponent<{
	company: CompanyEnrichPreviewExperienceCompany | null;
}> = ({ company }) => {
	if (!company?.name) {
		return null;
	}

	return (
		<span className="flex min-w-0 items-center gap-2">
			<CompanyLogoAvatar logoUrl={company.logo_url ?? null} name={company.name} size="sm" />
			<span className="flex min-w-0 flex-col leading-tight">
				<span className="truncate font-semibold">{company.name}</span>
				{company.domain && (
					<a
						className="truncate text-[11px] text-text-meta hover:text-brand-light hover:underline"
						href={`https://${company.domain}`}
						rel="noreferrer"
						target="_blank"
						onClick={(event) => event.stopPropagation()}
					>
						{company.domain}
					</a>
				)}
			</span>
		</span>
	);
};

/**
 * A person added by pasting a profile URL. There is no enrich record behind them, so the row is
 * built from the URL itself: the slug carries a readable name and the initials avatar makes it read
 * as a person rather than the bare URL string it used to print (PREC-3823).
 */
const ManualUrlCell: FunctionComponent<{ url: string }> = ({ url }) => {
	const name = parseLinkedinProfileUrl(url)?.name ?? url;

	return (
		<span className="flex min-w-0 items-center gap-2">
			<Avatar firstName={nameParts(name).firstName} lastName={nameParts(name).lastName} />
			<span className="flex min-w-0 flex-col leading-tight">
				<span className="truncate font-semibold">{name}</span>
				<a
					className="truncate text-[11px] text-text-meta hover:text-brand-light hover:underline"
					href={url}
					rel="noreferrer"
					target="_blank"
					onClick={(event) => event.stopPropagation()}
				>
					{url}
				</a>
			</span>
		</span>
	);
};

/**
 * Shown when a search matched nobody. Without it the grid is a bare header row
 * with no hint that the filters — not the step — are what came up empty.
 */
const EmptyRow: FunctionComponent<{
	title: string;
	hint: string;
	retryLabel?: string;
	onRetry?: () => void;
}> = ({ title, hint, retryLabel, onRetry }) => (
	<tr>
		<td className={classNames(cellClass, "py-8 text-center")} colSpan={5}>
			<div className="font-semibold">{title}</div>
			<div className="mt-1 text-xs text-text-meta">{hint}</div>
			{onRetry && (
				<button
					className="mt-2 cursor-pointer text-xs font-semibold text-brand-light"
					type="button"
					onClick={onRetry}
				>
					{retryLabel}
				</button>
			)}
		</td>
	</tr>
);

/** Mockup-style people grid: Name | Company | Position. */
export const TrackPeopleTable: FunctionComponent<{
	people: CompanyEnrichPreviewPerson[];
	selectedIds: Set<number>;
	onToggle: (id: number) => void;
	onRemove: (id: number) => void;
	onAddPerson: (person: CompanyEnrichPreviewPerson) => void;
	manualUrls: string[];
	onManualUrlsChange: (urls: string[]) => void;
	/** The suggestions could not be built, so the empty table is not an answer. */
	hasLoadFailed?: boolean;
	onRetryLoad?: () => void;
}> = ({
	people,
	selectedIds,
	onToggle,
	onRemove,
	onAddPerson,
	manualUrls,
	onManualUrlsChange,
	hasLoadFailed,
	onRetryLoad,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.track.${key}`);
	const [isAdding, setIsAdding] = useState(false);

	const addManualUrl = (url: string) => {
		if (!manualUrls.includes(url)) {
			onManualUrlsChange([...manualUrls, url]);
		}

		setIsAdding(false);
	};

	const addPerson = (person: CompanyEnrichPreviewPerson) => {
		onAddPerson(person);
		setIsAdding(false);
	};

	// `max-h-full` + `min-h-0` are what keep the table inside the space the step gives it: it is a
	// flex item of a card that is `max-h-[92vh]`, so on a short viewport the row it sits in is
	// smaller than the rows it holds. Without them the box kept its full height and painted over the
	// footer and out through the bottom of the card. Same shape as the filter rail beside it.
	return (
		<div className="flex max-h-full min-h-0 min-w-0 flex-1 flex-col self-start overflow-hidden rounded-xl border border-gray-200">
			<div className="min-h-0 max-h-[480px] flex-1 overflow-y-auto">
				<table className="w-full table-fixed">
					<thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
						<tr>
							{/* The count the footer shows, so the two never disagree — a pasted profile URL is
							    a pick like any other. */}
							<th className={classNames(headerClass, "w-11")}>
								{selectedIds.size + manualUrls.length || "#"}
							</th>
							<th className={classNames(headerClass, "w-[28%]")}>{ts("colName")}</th>
							<th className={classNames(headerClass, "w-[28%]")}>{ts("colCompany")}</th>
							<th className={classNames(headerClass, "w-[34%]")}>{ts("colPosition")}</th>
							<th className={classNames(headerClass, "w-10")} />
						</tr>
					</thead>
					<tbody>
						{people.length === 0 &&
							manualUrls.length === 0 &&
							// A failed search must not be reported as "nobody matched": the filters
							// are not what came up empty, and widening them will not help.
							(hasLoadFailed ? (
								<EmptyRow
									hint={ts("failedHint")}
									retryLabel={ts("failedRetry")}
									title={ts("failedTitle")}
									onRetry={onRetryLoad}
								/>
							) : (
								<EmptyRow hint={ts("emptyHint")} title={ts("emptyTitle")} />
							))}
						{people.map((person, index) => (
							<tr
								key={person.id}
								className={classNames(
									"group cursor-pointer border-b border-gray-100 last:border-b-0",
									selectedIds.has(person.id) ? "bg-blue-action/5" : "hover:bg-gray-50"
								)}
								onClick={() => onToggle(person.id)}
							>
								<RowNumberCell
									index={index}
									isSelected={selectedIds.has(person.id)}
									label={person.name}
									onToggle={() => onToggle(person.id)}
								/>
								<td className={cellClass}>
									<span className="flex min-w-0 items-center gap-2 font-semibold">
										<Avatar
											firstName={nameParts(person.name).firstName}
											imageSrc={person.image_url ?? undefined}
											lastName={nameParts(person.name).lastName}
										/>
										<span className="truncate">{person.name}</span>
									</span>
								</td>
								<td className={cellClass}>
									<CompanyCell company={displayExperience(person)?.company ?? null} />
								</td>
								<td
									className={classNames(cellClass, "truncate")}
									title={headlineOf(person) ?? jobTitleOf(person) ?? undefined}
								>
									{jobTitleOf(person)}
								</td>
								<td className={classNames(cellClass, "w-10")}>
									<button
										aria-label={ts("removeRow")}
										className="cursor-pointer text-text-meta opacity-0 transition-opacity duration-150 hover:text-status-error group-hover:opacity-100"
										type="button"
										onClick={(event) => {
											event.stopPropagation();
											onRemove(person.id);
										}}
									>
										<Icon icon={faXmark} />
									</button>
								</td>
							</tr>
						))}
						{manualUrls.map((url) => (
							<tr key={url} className="border-b border-gray-100 bg-blue-action/5 last:border-b-0">
								<td className={classNames(cellClass, "w-11 text-center text-brand-light")}>✓</td>
								<td className={cellClass} colSpan={3}>
									<ManualUrlCell url={url} />
								</td>
								<td className={classNames(cellClass, "w-10")}>
									<button
										aria-label={ts("removeRow")}
										className="cursor-pointer text-text-meta hover:text-status-error"
										type="button"
										onClick={() => onManualUrlsChange(manualUrls.filter((entry) => entry !== url))}
									>
										<Icon icon={faXmark} />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{isAdding ? (
				<div className="flex shrink-0 items-center gap-2 border-t border-dashed border-gray-200 px-3 py-2">
					<TrackAddPersonCombobox onSelectPerson={addPerson} onSelectUrl={addManualUrl} />
				</div>
			) : (
				<button
					className="w-full shrink-0 cursor-pointer border-t border-dashed border-gray-200 px-3 py-2.5 pl-11 text-left text-xs font-semibold text-brand-light hover:bg-gray-50"
					type="button"
					onClick={() => setIsAdding(true)}
				>
					+ {ts("addPerson")}
				</button>
			)}
		</div>
	);
};
